// =============================================================================
// DEVIS & FACTURES - Liste des devis avec brouillons
// Migré vers OHGrid v1.1.1 (GROOT UI-002/004/005/006/007)
// =============================================================================
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FileText, Clock, CheckCircle2, AlertTriangle, Eye, Edit, Trash2, Lock, Send,
  Receipt, RefreshCw, Calendar, Euro, User, Unlock, FileCheck, Banknote,
  Archive, ArchiveRestore, Download,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
// 🔑 LA garde canonique des capabilities — la MÊME que le menu (OmniHub.jsx) et
// que `EnvoyerVisiteBlock`. ⛔ Aucun slug, ⛔ aucun second mécanisme.
import { usePermissions } from "../../contexts/PermissionContext";
import { OHGrid } from "../../components/ui/oh-grid";
import { isInstallateurProfile } from "../../utils/tenantProfile";
import SendDevisOmvModal from "../../components/omv/SendDevisOmvModal";
import DevisEnvoiTrace from "../../components/omv/DevisEnvoiTrace";
import { withDevisPrefix, devisPrefixEnabled } from "../../utils/devisDisplay";
import { devisStatutKey, devisStatutLabel, DEVIS_STATUT_DEFAULTS } from "../../utils/devisStatut";
import { useConfigList } from "../../hooks/useConfigList";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Mapping des statuts avec niveaux de verrouillage
const STATUT_CONFIG = {
  draft: { label: "Brouillon", color: "bg-zinc-500/20 border border-[var(--oh-border)]", icon: Edit, verrouille: false },
  validation: { label: "En validation", color: "bg-amber-500", icon: Clock, verrouille: false },
  locked: { label: "Verrouillé (Niveau 1)", color: "bg-[var(--oh-accent-solid)]", icon: Lock, verrouille: true, peutDeverrouiller: true },
  devis_signe: { label: "Devis signé (Niveau 2)", color: "bg-emerald-500", icon: FileCheck, verrouille: true, peutDeverrouiller: true },
  dossier_depose: { label: "Dossier déposé", color: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]", icon: Send, verrouille: true, peutDeverrouiller: true },
  paye: { label: "Payé (Final)", color: "bg-green-600", icon: Banknote, verrouille: true, peutDeverrouiller: false },
  envoye: { label: "Envoyé", color: "bg-[var(--oh-accent-solid)]", icon: Send, verrouille: false },
  signe: { label: "Signé", color: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", icon: CheckCircle2, verrouille: true, peutDeverrouiller: true },
  archive: { label: "Archivé", color: "bg-[var(--oh-accent-solid)]", icon: FileText, verrouille: true, peutDeverrouiller: false },
  refuse: { label: "Refusé", color: "bg-red-500", icon: AlertTriangle, verrouille: false },
  annule: { label: "Annulé", color: "bg-[var(--oh-bg-elevated)]", icon: Trash2, verrouille: false },
};

const peutDeverrouiller = (statut) => ["locked", "devis_signe", "dossier_depose"].includes(statut);

export default function DevisFacturesPage({ tenantId, onNavigate }) {
  // 🔴 L'ONGLET FACTURES — ⛔ AUCUNE réorganisation de hub. La facture
  // appartient au domaine Finance dans le modèle, mais la navigation attend le
  // chantier produits/capacités. En attendant, elle vit LÀ OÙ ELLE NAÎT :
  // dans le parcours du devis qui la produit.
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterStatut, setFilterStatut] = useState("all");
  const [sendRow, setSendRow] = useState(null);  // devis OMV en cours d'envoi email
  // 🔴 LE GESTE « FACTURER ». `FactureService.creer_depuis_devis` existait
  // depuis le 26/08 avec ZÉRO appelant : les 4 factures natives ont été
  // produites par script. Un service sans chemin utilisateur n'est pas livré.
  // ⚠️ L'éligibilité vient du SERVEUR (`POST /factures/eligibilite`), en UN
  // appel pour toute la grille. ⛔ On ne rejoue pas la règle ici : le seuil
  // « quel statut de devis se facture » a déjà DEUX vocabulaires en base, un
  // troisième dans le navigateur finirait par gagner en silence.
  const [eligibilites, setEligibilites] = useState({});
  const { items: statutsDevis } = useConfigList("statuts_devis");

  const isTEF = tenantId === "tef" || tenantId === "demo";
  // Profil installateur (OMV, Climat) — pilote les 3 options devis (Aperçu / PDF /
  // Modifier). Gating par PROFIL (business_model), pas par tenant. TEF (AMO) exclu.
  const isInstallateur = isInstallateurProfile();

  // ─── Helpers format ───
  const formatPrice = (v) => v != null
    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v)
    : "0,00 €";
  const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

  // ─── Chargement (useCallback AVANT useEffect — anti-TDZ) ───
  const loadDevis = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("session_token");
      const headers = {
        "X-Tenant-ID": tenantId || "climatpac",
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      const res = await fetch(`${API}/api/metier/devis`, { headers, credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const list = isTEF
          ? data.filter(d => d.type_document === "devis_amo" || d.type_document === "contrat_amo")
          : data;
        // `id` stable pour OHGrid (déjà présent dans la data)
        setDevis(list);
      } else {
        console.error("Erreur API devis:", res.status);
      }
    } catch (error) {
      console.error("Erreur chargement devis:", error);
      toast.error("Erreur lors du chargement des devis");
    } finally {
      setLoading(false);
    }
  }, [tenantId, isTEF]);

  useEffect(() => { loadDevis(); }, [loadDevis]);

  /** L'état de facturation de TOUTE la grille, en UN appel.
   *  ⛔ Pas une requête par ligne : 148 devis feraient 148 requêtes pour
   *  décider de l'affichage d'un bouton. */
  const loadEligibilites = useCallback(async (liste) => {
    const ids = (liste || []).map(d => d.id).filter(Boolean);
    if (!ids.length) { setEligibilites({}); return; }
    try {
      const res = await fetch(`${API}/api/metier/factures/eligibilite`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ devis_ids: ids }),
      });
      if (!res.ok) return;              // ⚠️ silencieux : sans état, pas de bouton
      const data = await res.json();
      setEligibilites(data.etats || {});
    } catch { /* l'absence d'état masque le bouton — ⛔ jamais un faux « éligible » */ }
  }, []);

  useEffect(() => { loadEligibilites(devis); }, [devis, loadEligibilites]);

  /** 🔴 LE GESTE. Devis éligible → facture BROUILLON, sans ressaisie, puis on
   *  OUVRE la facture créée. ⛔ Aucun refus muet : le motif du serveur s'affiche. */
  const { isFeatureEnabled } = usePermissions();
  const facturationActive = isFeatureEnabled("commercial.factures");

  const handleFacturer = useCallback(async (row) => {
    const etat = eligibilites[row.id];
    if (etat && !etat.eligible) { toast.error(etat.motif); return; }
    try {
      const res = await fetch(`${API}/api/metier/factures/depuis-devis`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ devis_id: row.id }),
      });
      const data = await res.json();
      if (!res.ok) {
        // 🔑 LE DOUBLE CLIC ARRIVE ICI : la 2ᵉ requête reçoit 409 et le motif
        // nomme la facture déjà créée. ⛔ Pas « erreur », pas un silence.
        toast.error(data.detail || `Facturation refusée (${res.status})`);
        loadEligibilites(devis);
        return;
      }
      if (data.mentions_manquantes?.length) {
        toast.warning(`Facture créée — mentions à compléter avant émission : `
                      + data.mentions_manquantes.join(", "));
      } else {
        toast.success(data.message || "Facture brouillon créée");
      }
      // 🔑 ON AMÈNE L'UTILISATEUR À SA FACTURE, dans l'entrée de menu native.
      // ⛔ Pas « elle est quelque part dans la liste ».
      onNavigate?.("factures", { factureId: data.id });
      loadEligibilites(devis);
    } catch (e) {
      toast.error("Facturation impossible : " + e.message);
    }
  }, [eligibilites, devis, loadEligibilites, onNavigate]);

  const handleVoirFacture = useCallback((row) => {
    const etat = eligibilites[row.id];
    if (!etat?.facture_id) return;
    onNavigate?.("factures", { factureId: etat.facture_id });
  }, [eligibilites, onNavigate]);

  // ─── Filtrage selon filterType/filterStatut/showArchived ───
  const visibleDevis = useMemo(() => devis.filter(d => {
    if (!showArchived && d.statut === "archive") return false;
    // OMV : filtre sur la clé WORKFLOW (devisStatutKey) — les options reflètent les
    // vrais statuts OMV. TEF : filtre sur le statut brut de l'enum backend.
    if (filterStatut !== "all") {
      const matches = isInstallateur ? devisStatutKey(d) === filterStatut : d.statut === filterStatut;
      if (!matches) return false;
    }
    if (filterType === "brouillons" && !(d.statut === "draft" || d.statut === "validation")) return false;
    if (filterType === "valides" && !(d.statut === "locked" || d.statut === "signe")) return false;
    return true;
  }), [devis, showArchived, filterStatut, filterType, isInstallateur]);

  // ─── Actions unitaires ───
  const handleContinue = useCallback((row) => {
    if (isTEF || !onNavigate) return;
    window.__devisId = row.id;
    window.__dossierId = row.dossier_id;
    onNavigate("devis-wizard", { devisId: row.id, dossierId: row.dossier_id });
  }, [isTEF, onNavigate]);

  // Ouvrir un devis = VUE DOCUMENT (DevisDocument), PAS le wizard.
  // Le wizard reste réservé à l'action "Continuer / Modifier" (handleContinue).
  const handleView = useCallback((row) => {
    if (isTEF || !onNavigate) return;
    window.__devisId = row.id;
    window.__dossierId = row.dossier_id;
    onNavigate("devis-document", { devisId: row.id, dossierId: row.dossier_id, from: "devis-factures" });
  }, [isTEF, onNavigate]);

  // OMV : envoi/renvoi du devis par email (même modal que la fiche/aperçu).
  const handleSendEmail = useCallback((row) => setSendRow(row), []);

  const handleDownloadAmoPdf = useCallback(async (row) => {
    try {
      if (!row.pdf_url) { toast.error("Aucun PDF disponible pour ce devis"); return; }
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}${row.pdf_url}`, {
        headers: {
          "X-Tenant-ID": tenantId || "tef",
          ...(token && { Authorization: `Bearer ${token}` }),
        }, credentials: "include",
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${row.type_document || "devis"}_${row.numero || row.id}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else toast.error("Erreur lors du téléchargement du PDF");
    } catch (err) { console.error(err); toast.error("Erreur réseau"); }
  }, [tenantId]);

  // Installateur (OMV/Climat) — PDF d'aperçu : même endpoint que DevisDocument
  // (POST /documents/devis/{id}/pdf/preview → pdf_base64). Les devis installateur
  // n'ont pas de pdf_url AMO ; on télécharge donc le rendu d'aperçu.
  const handleDownloadPreview = useCallback(async (row) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/documents/devis/${row.id}/pdf/preview`, {
        method: "POST",
        headers: {
          "X-Tenant-ID": tenantId || localStorage.getItem("current_tenant_slug") || "",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        credentials: "include",
      });
      if (!res.ok) { toast.error("Aperçu PDF indisponible"); return; }
      const d = await res.json();
      if (!d.pdf_base64) { toast.error("Aperçu PDF indisponible"); return; }
      const a = document.createElement("a");
      a.href = `data:application/pdf;base64,${d.pdf_base64}`;
      a.download = `Devis_${String(row.numero || row.id).replace(/\//g, "-")}.pdf`;
      a.click();
    } catch (err) { console.error(err); toast.error("Erreur réseau pendant le téléchargement du PDF"); }
  }, [tenantId]);

  const handleDelete = useCallback(async (row) => {
    if (!window.confirm("Supprimer ce brouillon ?")) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/devis/${row.id}`, {
        method: "DELETE",
        headers: {
          "X-Tenant-ID": tenantId || "climatpac",
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      if (res.ok) { toast.success("Brouillon supprimé"); loadDevis(); }
      else toast.error("Erreur lors de la suppression");
    } catch (err) { console.error(err); toast.error("Erreur réseau"); }
  }, [tenantId, loadDevis]);

  const handleArchive = useCallback(async (row) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/devis/${row.id}/archiver`, {
        method: "POST",
        headers: {
          "X-Tenant-ID": tenantId || "climatpac",
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        }, credentials: "include",
      });
      if (res.ok) { toast.success("Devis archivé"); loadDevis(); }
      else toast.error("Erreur archivage");
    } catch { toast.error("Erreur réseau"); }
  }, [tenantId, loadDevis]);

  const handleUnarchive = useCallback(async (row) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/devis/${row.id}/desarchiver`, {
        method: "POST",
        headers: {
          "X-Tenant-ID": tenantId || "climatpac",
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        }, credentials: "include",
      });
      if (res.ok) { toast.success("Devis désarchivé"); loadDevis(); }
      else toast.error("Erreur désarchivage");
    } catch { toast.error("Erreur réseau"); }
  }, [tenantId, loadDevis]);

  const handleDeverrouiller = useCallback(async (row) => {
    if (!peutDeverrouiller(row.statut)) { toast.error("Ce devis ne peut pas être déverrouillé"); return; }
    if (row.statut === "paye") { toast.error("Un devis payé ne peut jamais être déverrouillé"); return; }
    const motif = window.prompt("⚠️ DÉVERROUILLAGE ADMIN\n\nMotif (min 10 caractères):");
    if (!motif || motif.length < 10) { toast.error("Motif obligatoire (10 car. min)"); return; }
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/metier/devis/${row.id}/deverrouiller`, {
        method: "POST",
        headers: {
          "X-Tenant-ID": tenantId || "climatpac",
          "Content-Type": "application/json",
          "X-Admin-Action": "true",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ motif, commentaire: "Déverrouillage depuis interface" }),
      });
      const data = await res.json();
      if (res.ok && data.success) { toast.success(`Déverrouillé: ${data.statut_avant} → ${data.statut_apres}`); loadDevis(); }
      else toast.error(data.detail || "Erreur déverrouillage");
    } catch { toast.error("Erreur réseau"); }
  }, [tenantId, loadDevis]);

  // ─── Bulk actions ───
  // body (optionnel) : corps JSON transmis à CHAQUE appel (ex. {template_key} pour
  // l'envoi de devis). Sans body → comportement historique (archiver/supprimer…).
  const bulkRun = useCallback(async (ids, url, method = "POST", body = null) => {
    const token = localStorage.getItem("session_token");
    let ok = 0, errs = 0;
    for (const id of ids) {
      try {
        const res = await fetch(`${API}${url.replace(":id", id)}`, {
          method,
          headers: {
            "X-Tenant-ID": tenantId || "climatpac",
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          ...(body ? { body: JSON.stringify(body) } : {}),
          credentials: "include",
        });
        if (res.ok) ok++; else errs++;
      } catch { errs++; }
    }
    return { ok, errs };
  }, [tenantId]);

  const handleBulkArchive = useCallback(async (ids) => {
    if (!window.confirm(`Archiver ${ids.length} devis ?`)) return;
    const { ok, errs } = await bulkRun(ids, "/api/metier/devis/:id/archiver");
    if (ok) toast.success(`${ok} devis archivé(s)`);
    if (errs) toast.error(`${errs} erreur(s)`);
    loadDevis();
  }, [bulkRun, loadDevis]);

  const handleBulkUnarchive = useCallback(async (ids) => {
    if (!window.confirm(`Désarchiver ${ids.length} devis ?`)) return;
    const { ok, errs } = await bulkRun(ids, "/api/metier/devis/:id/desarchiver");
    if (ok) toast.success(`${ok} devis désarchivé(s)`);
    if (errs) toast.error(`${errs} erreur(s)`);
    loadDevis();
  }, [bulkRun, loadDevis]);

  const handleBulkDelete = useCallback(async (ids) => {
    const selectedDevis = devis.filter(d => ids.includes(d.id));
    const nonSupprimables = selectedDevis.filter(d => !["draft", "archive", "annule"].includes(d.statut));
    if (nonSupprimables.length > 0) {
      toast.error(`${nonSupprimables.length} devis ne peuvent pas être supprimés (seuls brouillons/archivés/annulés)`);
      return;
    }
    if (!window.confirm(`Supprimer définitivement ${ids.length} devis ?`)) return;
    const { ok, errs } = await bulkRun(ids, "/api/metier/devis/:id", "DELETE");
    if (ok) toast.success(`${ok} devis supprimé(s)`);
    if (errs) toast.error(`${errs} erreur(s)`);
    loadDevis();
  }, [devis, bulkRun, loadDevis]);

  // ─── Bulk envoi / renvoi devis OMV (CC-DEV1-21) ───
  // Éligibilité (selon le marqueur email_envoi, source unique de l'état d'envoi) :
  //   « Envoyer »  → devis PAS encore envoyé : statut "emis" ET email_envoi.status ≠ "sent".
  //   « Renvoyer » → devis DÉJÀ envoyé : email_envoi.status === "sent".
  const _isSent = (d) => d?.email_envoi?.status === "sent";
  const _isEnvoyable = (d) => d?.statut === "emis" && !_isSent(d);
  const _isRenvoyable = (d) => _isSent(d);

  // template_key UNIQUE pour le lot : MÊME source que SendDevisOmvModal (templates
  // "devis" du tenant, Design Studio). Priorité au template envoi_devis (corrigé
  // CC-SAV-04/CC-DEV1-20), sinon le 1er template devis du tenant.
  const resolveDevisTemplateKey = useCallback(async () => {
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/api/governance/email-config/templates?owner=design_studio&category=devis`, {
        headers: {
          "X-Tenant-ID": tenantId || "climatpac",
          ...(token && { Authorization: `Bearer ${token}` }),
        }, credentials: "include",
      });
      const data = await res.json();
      const list = (data?.templates || []).filter((t) => t.active !== false);
      const tpl = list.find((t) => /envoi_devis/i.test(t.key || "")) || list[0];
      return tpl?.key || null;
    } catch { return null; }
  }, [tenantId]);

  const handleBulkSend = useCallback(async (ids, mode) => {
    // mode: "send" (Envoyer) | "resend" (Renvoyer)
    const selected = devis.filter((d) => ids.includes(d.id));
    const eligible = selected.filter(mode === "send" ? _isEnvoyable : _isRenvoyable);
    const ignored = selected.length - eligible.length;
    const verb = mode === "send" ? "Envoyer" : "Renvoyer";
    if (!eligible.length) {
      toast.info(`Aucun devis éligible à « ${verb} » — ${ignored} ignoré(s) (déjà envoyé ou non éligible)`);
      return;
    }
    const templateKey = await resolveDevisTemplateKey();
    if (!templateKey) { toast.error("Aucun template d'envoi de devis (Design Studio → Emails Métier)"); return; }
    if (!window.confirm(`${verb} ${eligible.length} devis ?${ignored ? ` (${ignored} ignoré[s] : déjà envoyé ou non éligible)` : ""}`)) return;
    const { ok, errs } = await bulkRun(eligible.map((d) => d.id), "/api/metier/devis/:id/send-email", "POST", { template_key: templateKey });
    const done = mode === "send" ? "envoyé(s)" : "renvoyé(s)";
    toast.success(`${ok} ${done} · ${ignored} ignoré(s)${errs ? ` · ${errs} erreur(s)` : ""}`);
    loadDevis();
  }, [devis, bulkRun, resolveDevisTemplateKey, loadDevis]);

  // ─── Config OHGrid ───
  const stats = useMemo(() => {
    const all = visibleDevis;
    // OMV : KPIs PAR STATUT (workflow Brouillon→Payé) + Total — pour comparer aux
    // dossiers et comprendre les écarts. Compte via la clé workflow (devisStatutKey).
    if (isInstallateur) {
      const order = (statutsDevis && statutsDevis.length ? statutsDevis : DEVIS_STATUT_DEFAULTS);
      const palette = { brouillon: "gray", valide: "blue", envoye: "blue", signe: "green",
        en_cours: "amber", a_facturer: "purple", facture: "purple", paye: "green" };
      const cards = order.map((it) => {
        const id = it.id || it.value;
        return {
          key: `st_${id}`, label: it.label || id,
          value: all.filter((d) => devisStatutKey(d) === id).length,
          color: palette[id] || "gray",
          filter: (row) => devisStatutKey(row) === id,
        };
      });
      // Archivés : comptés sur la liste BRUTE (devis) — incluse même quand le toggle
      // "Inclure archivés" est off (la liste backend ne filtre pas archive).
      cards.push({ key: "archive", label: "Archivés",
        value: devis.filter((d) => d.statut === "archive").length,
        icon: Archive, color: "amber", filter: (row) => row.statut === "archive" });
      cards.push({ key: "total", label: "Total", value: all.length, icon: Receipt, color: "blue", filter: null });
      return cards;
    }
    const totalCASigne = all.filter(d => d.statut === "signe").reduce((s, d) => s + (d.total_ttc || 0), 0);
    return [
      { key: "brouillons", label: "Brouillons", value: all.filter(d => d.statut === "draft").length,
        icon: Edit, color: "gray", filter: (row) => row.statut === "draft" },
      { key: "verrouilles", label: "Verrouillés", value: all.filter(d => d.statut === "locked").length,
        icon: Lock, color: "blue", filter: (row) => row.statut === "locked" },
      { key: "signes", label: "Signés", value: all.filter(d => d.statut === "signe").length,
        icon: CheckCircle2, color: "green", filter: (row) => row.statut === "signe" },
      { key: "ca", label: "CA signés", value: formatPrice(totalCASigne), icon: Euro, color: "purple", filter: null },
    ];
  }, [visibleDevis, devis, isInstallateur, statutsDevis]);

  // Options du filtre statut : OMV = vrais statuts workflow (liste éditable
  // statuts_devis, repli DEVIS_STATUT_DEFAULTS) ; TEF = statuts de l'enum backend.
  // Fin du hardcode TEF (draft/validation/locked/signe/refuse/annule) sur OMV.
  const statutFilterOptions = useMemo(() => {
    if (isInstallateur) {
      const order = (statutsDevis && statutsDevis.length ? statutsDevis : DEVIS_STATUT_DEFAULTS);
      return order.map((it) => ({ value: it.id || it.value, label: it.label || it.id }));
    }
    return [
      { value: "draft", label: "Brouillon" },
      { value: "validation", label: "En validation" },
      { value: "locked", label: "Verrouillé" },
      { value: "signe", label: "Signé" },
      { value: "refuse", label: "Refusé" },
      { value: "annule", label: "Annulé" },
    ];
  }, [isInstallateur, statutsDevis]);

  const advancedFilters = useMemo(() => [
    { key: "filterType", label: "Type", type: "select", defaultValue: "all",
      options: [
        { value: "brouillons", label: "Brouillons uniquement" },
        { value: "valides", label: "Validés uniquement" },
      ] },
    { key: "filterStatut", label: "Statut", type: "select", defaultValue: "all",
      options: statutFilterOptions },
    { key: "showArchived", label: "Inclure archivés", type: "checkbox", defaultValue: false },
  ], [statutFilterOptions]);

  const handleAdvFilter = useCallback(({ key, value }) => {
    if (key === "filterType") setFilterType(value);
    if (key === "filterStatut") setFilterStatut(value);
    if (key === "showArchived") setShowArchived(value);
  }, []);

  const searchFilter = useCallback((row, q) => (
    (row.numero || "").toLowerCase().includes(q) ||
    (row.numero_provisoire || "").toLowerCase().includes(q) ||
    (row.objet || "").toLowerCase().includes(q) ||
    (row.client_nom || "").toLowerCase().includes(q)
  ), []);

  const columns = useMemo(() => [
    {
      key: "numero", label: "Numéro / Objet",
      render: (v, row) => {
        const num = row.numero || row.numero_provisoire;
        // Préfixe « Devis - » VISUEL pour OMV (valeur stockée nue, recherche/tri intacts).
        const label = num
          ? (devisPrefixEnabled() ? withDevisPrefix(num) : num)
          : (isTEF ? (row.type_document === "contrat_amo" ? "Contrat AMO" : "Devis AMO") : `BROUILLON-${row.id?.slice(-6)}`);
        return (
        <div>
          <p className="font-medium text-[var(--oh-text)]">{label}</p>
          <p className="text-xs text-[var(--oh-text-secondary)] truncate max-w-xs">
            {isTEF && row.type_document && (
              <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] border border-[var(--oh-info-border)] text-xs mr-1">
                {row.type_document === "contrat_amo" ? "Contrat AMO" : "Devis AMO"}
              </Badge>
            )}
            {row.objet || (isTEF ? "" : "Sans objet")}
          </p>
          {/* Trace dernier envoi email (criterion : visible aussi dans la grille). */}
          {row.email_envoi && <DevisEnvoiTrace envoi={row.email_envoi} />}
        </div>
        );
      },
    },
    {
      key: "dossier_numero", label: "Dossier", sortable: false,
      render: (_, row) => (row.dossier_numero || row.dossier_id) ? (
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[var(--oh-text-secondary)]" />
          <span className="text-sm text-[var(--oh-text-muted)]">
            {row.dossier_numero || `#${row.dossier_id?.slice(-6) || "?"}`}
          </span>
          {row.is_principal && <Badge className="bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] text-xs">★</Badge>}
        </div>
      ) : <span className="text-xs text-[var(--oh-text-secondary)]">—</span>,
    },
    {
      key: "client_nom", label: "Client",
      render: (v) => (
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-[var(--oh-text-secondary)]" />
          <span className="text-sm text-[var(--oh-text-secondary)]">{v || "Client non défini"}</span>
        </div>
      ),
    },
    // D7 — CUMAC AVANT Montant TTC (ordre demandé JJ). Gaté installateur.
    // CUMAC = volume_kwh_cumac (joint depuis le dossier parent, cf. backend).
    ...(isInstallateur ? [
      {
        key: "volume_kwh_cumac", label: "CUMAC",
        render: (v) => <span className="text-sm text-[var(--oh-text-secondary)]">{v != null ? `${Number(v).toLocaleString("fr-FR")} kWh` : "—"}</span>,
      },
    ] : []),
    {
      key: "total_ttc", label: "Montant TTC",
      render: (v) => <span className="font-medium text-[var(--oh-success-text)]">{formatPrice(v)}</span>,
    },
    // Prime CEE (= aides_deduites) après Montant TTC. Gaté installateur.
    ...(isInstallateur ? [
      {
        key: "montant_prime_cee", label: "Prime CEE",
        render: (v) => <span className="font-medium text-cyan-400">{v ? formatPrice(v) : "—"}</span>,
      },
    ] : []),
    {
      key: "statut", label: "Statut",
      render: (v, row) => {
        const cfg = STATUT_CONFIG[v] || STATUT_CONFIG.draft;
        const Icon = cfg.icon;
        return (
          <Badge className={`${cfg.color} text-[var(--oh-text)] flex items-center gap-1 w-fit`}>
            <Icon className="w-3 h-3" /> {devisStatutLabel(row, statutsDevis, cfg.label)}
          </Badge>
        );
      },
    },
    {
      key: "updated_at", label: "Date",
      render: (v, row) => (
        <div className="flex items-center gap-1 text-xs text-[var(--oh-text-secondary)]">
          <Calendar className="w-3 h-3" /> {formatDate(v || row.created_at)}
        </div>
      ),
    },
  ], [isTEF, isInstallateur]);

  const rowActions = useMemo(() => [
    { label: "Télécharger PDF", icon: Download,
      onClick: handleDownloadAmoPdf,
      show: () => isTEF, className: "text-[var(--oh-info-text)]" },
    // Installateur (OMV/Climat) : 3 options PDF / Aperçu / Modifier.
    // Aperçu (= vue document) désormais visible AUSSI sur les brouillons.
    { label: "Aperçu", icon: Eye,
      onClick: handleView,
      show: (row) => isInstallateur },
    { label: "PDF", icon: Download,
      onClick: handleDownloadPreview,
      show: (row) => isInstallateur, className: "text-[var(--oh-info-text)]" },
    { label: "Envoyer / Renvoyer par email", icon: Send,
      onClick: handleSendEmail,
      show: (row) => isInstallateur && row.statut === "emis",
      className: "text-[var(--oh-accent-text)]" },
    // 🔴 FACTURER — le geste qui manquait. Visible sur un devis ÉLIGIBLE, et
    // l'éligibilité est celle du SERVEUR. ⛔ Pas de seuil recopié ici.
    // 🔴 VERROU — la conversion Devis→Facture ⛔ n'est PAS qualifiée partout.
    // La CAPACITÉ DÉCLARÉE `commercial.factures` commande, ⛔ pas un slug.
    // ⚠️ `isFeatureEnabled` rend `false` TANT QUE le contexte charge (DEC-079 :
    // unknown ≠ enabled) ⇒ l'action ⛔ n'apparaît JAMAIS par intermittence.
    { label: "Facturer", icon: Euro,
      onClick: handleFacturer,
      show: (row) => facturationActive && eligibilites[row.id]?.eligible === true,
      className: "text-[var(--oh-success-text)]" },
    // ⚠️ Le devis DÉJÀ facturé ne perd pas son chemin : il mène à sa facture.
    // ⛔ Un bouton qui disparaît sans rien dire laisse croire à une perte.
    { label: "Voir la facture", icon: Receipt,
      onClick: handleVoirFacture,
      show: (row) => Boolean(eligibilites[row.id]?.facture_id),
      className: "text-[var(--oh-accent-text)]" },
    { label: "Continuer / Modifier", icon: Edit,
      onClick: handleContinue,
      show: (row) => isInstallateur && ["draft", "validation"].includes(row.statut),
      className: "text-[var(--oh-success-text)]" },
    { label: "Archiver", icon: Archive,
      onClick: handleArchive,
      show: (row) => row.statut !== "archive" && row.statut !== "paye",
      className: "text-[var(--oh-info-text)]" },
    { label: "Désarchiver", icon: ArchiveRestore,
      onClick: handleUnarchive,
      show: (row) => row.statut === "archive",
      className: "text-[var(--oh-accent-text)]" },
    { label: "Déverrouiller (admin)", icon: Unlock,
      onClick: handleDeverrouiller,
      show: (row) => !isTEF && peutDeverrouiller(row.statut),
      className: "text-[var(--oh-info-text)]" },
    { label: "Supprimer", icon: Trash2,
      onClick: handleDelete,
      show: (row) => row.statut === "draft" || row.statut === "archive" || row.statut === "annule",
      className: "text-[var(--oh-error-text)]" },
  ], [isTEF, isInstallateur, facturationActive, eligibilites, handleFacturer, handleVoirFacture, handleDownloadAmoPdf, handleDownloadPreview, handleContinue, handleView, handleSendEmail, handleArchive, handleUnarchive, handleDeverrouiller, handleDelete]);

  const bulkActions = useMemo(() => [
    // Envoyer / Renvoyer : OMV/Climat (profil installateur) — TEF gère ses devis AMO autrement.
    ...(isInstallateur ? [
      { label: "Envoyer", icon: Send, onClick: (ids) => handleBulkSend(ids, "send"),
        className: "border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]" },
      { label: "Renvoyer", icon: RefreshCw, onClick: (ids) => handleBulkSend(ids, "resend"),
        className: "border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]" },
    ] : []),
    { label: "Archiver", icon: Archive, onClick: handleBulkArchive,
      className: "border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]" },
    { label: "Désarchiver", icon: ArchiveRestore, onClick: handleBulkUnarchive,
      className: "border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]" },
    { label: "Supprimer", icon: Trash2, onClick: handleBulkDelete,
      className: "border-[var(--oh-error-border)] text-[var(--oh-error-text)] hover:bg-[var(--oh-error-bg)]" },
  ], [isInstallateur, handleBulkSend, handleBulkArchive, handleBulkUnarchive, handleBulkDelete]);

  return (
    <div className="space-y-6 p-6" data-testid="devis-factures-page">
      {/* 🔴 L'ONGLET « Factures » A ÉTÉ RETIRÉ D'ICI. Factures est désormais une
          ENTRÉE DE MENU native (décision CTO du 02/09) : le garder aussi en
          onglet aurait fait DEUX chemins vers le MÊME écran — exactement la
          duplication qu'on reproche ailleurs. Le geste « Facturer » navigue
          vers cette entrée, avec la facture ouverte. */}
      <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-[var(--oh-text)] flex items-center gap-2">
            <Receipt className="w-7 h-7 text-[var(--oh-success-text)]" />
            {isTEF ? "Devis AMO & Contrats AMO" : "Devis & Factures"}
          </h1>
          <p className="text-[var(--oh-text-secondary)]">
            {isTEF ? "Les devis AMO sont générés depuis la fiche dossier" : "Gérez vos devis et factures"}
          </p>
        </div>
        <Button variant="outline" onClick={loadDevis} className="border-[var(--oh-border)]" data-testid="devis-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
        </Button>
      </div>

      <OHGrid
        data={visibleDevis}
        columns={columns}
        stats={stats}
        advancedFilters={advancedFilters}
        onAdvancedFilter={handleAdvFilter}
        rowActions={rowActions}
        bulkActions={bulkActions}
        selectable={true}
        pagination={true}
        pageSize={25}
        loading={loading}
        onRefresh={loadDevis}
        onRowClick={(row) => {
          // Clic sur une ligne = ouvrir la VUE DOCUMENT (jamais le wizard).
          if (isTEF) handleDownloadAmoPdf(row);
          else handleView(row);
        }}
        searchPlaceholder="Rechercher par numéro, objet ou client..."
        searchFilter={searchFilter}
        defaultSortKey="updated_at"
        defaultSortDir="desc"
        emptyMessage="Aucun devis trouvé"
        testIdPrefix="devis"
      />

      <SendDevisOmvModal
        open={!!sendRow}
        onClose={() => setSendRow(null)}
        devis={sendRow}
        onSent={() => loadDevis()}
      />
      </>
    </div>
  );
}
